/* eslint-disable no-else-return */
/* eslint-disable no-unsafe-optional-chaining */

// Cache for expensive calculations
const widthCache = new Map<string, number>();
const optionsCache = new Map<string, any[]>();

const ROW_HEIGHT = 34;
const NAME_COLUMN_MIN_WIDTH = 150;
const PROPERTY_COLUMN_MIN_WIDTH = 130;

const calculatePropertyColumnWidth = (name: string) => {
  const cacheKey = `prop-${name}`;
  if (widthCache.has(cacheKey)) {
    return widthCache.get(cacheKey)!;
  }
  const result = Math.max(PROPERTY_COLUMN_MIN_WIDTH, (name?.length || 0) * 8 + 32);
  widthCache.set(cacheKey, result);
  return result;
};

// Optimized function to get unique options
const getUniqueOptions = (items: any[], key: string): any[] => {
  const cacheKey = `${key}-${items.length}`;
  if (optionsCache.has(cacheKey)) {
    return optionsCache.get(cacheKey)!;
  }

  const options = items
    .filter((item: any) => item?.[key] !== null && item?.[key] !== undefined)
    .reduce((accumulator: any, item: any) => {
      if (!accumulator.includes(item[key])) {
        accumulator.push(item[key]);
      }
      return accumulator;
    }, []);

  optionsCache.set(cacheKey, options);
  return options;
};

// Clear cache when data changes significantly
const clearCache = () => {
  widthCache.clear();
  optionsCache.clear();
};

const headerRow = (tableItemsProperty_: any, tableProperty_: any) => {
  // Early return for empty data
  if (!tableItemsProperty_?.length) {
    return {
      rowId: 'header',
      height: 38,
      cells: [
        {
          type: 'filter',
          options: [],
          id: 'name',
          text: 'Name',
          hasDelete: false,
          hasEdit: false,
          nonEditable: true,
          className: 'header-cell header-first',
          isModernFilter: true,
        },
      ],
    };
  }

  const nameOptions = getUniqueOptions(tableItemsProperty_, 'name');

  const cells = [
    {
      type: 'filter',
      options: nameOptions,
      id: 'name',
      text: 'Name',
      hasDelete: false,
      hasEdit: false,
      nonEditable: true,
      className: 'header-cell header-first',
      isModernFilter: true,
    },
  ];

  // Optimize property mapping
  if (tableProperty_?.length) {
    const propertyCells = tableProperty_.map((data: any) => {
      const options = getUniqueOptions(tableItemsProperty_, data?.id);
      return {
        type: 'filter',
        options,
        id: data?.id,
        hasDelete: true,
        hasEdit: true,
        colType: 'header',
        text: data?.name,
        className: 'header-cell',
        isModernFilter: true,
      };
    });
    cells.push(...propertyCells);
  }

  return {
    rowId: 'header',
    height: 38,
    cells,
  };
};

// Pre-computed date field names for faster comparison
const DATE_FIELD_NAMES = new Set(['start', 'end', 'start date', 'end date']);

const isDateField = (fieldName: string): boolean => {
  return DATE_FIELD_NAMES.has(fieldName.trim().toLowerCase());
};

const getRows = (tableItemsProperty_: any, tableProperty_: any) => {
  // Early return for empty data
  if (!tableItemsProperty_?.length) {
    return [headerRow(tableItemsProperty_, tableProperty_)];
  }

  const rows = [headerRow(tableItemsProperty_, tableProperty_)];

  // Optimize row creation with pre-allocated arrays
  const dataRows = tableItemsProperty_.map((item: any, index: any) => {
    const cells = [
      {
        type: 'text',
        placeholder: 'Enter value',
        text: `${item?.name}`,
        property: 'name',
        rows: tableItemsProperty_.length,
        className: 'name',
        hasFormulaError: false,
        hideTooltip: true,
        index,
      },
    ];

    // Optimize property cell creation
    if (tableProperty_?.length) {
      const propertyCells = tableProperty_.map((data: any) => {
        if (isDateField(data?.name)) {
          return {
            type: 'date',
            text: item[`${data?.id}`] || '',
            date: new Date(),
            nonEditable: !item?.id,
            isModernDate: true,
          };
        } else if (data?.items) {
          return {
            type: 'dropdown',
            nonEditable: !item?.id,
            selectOption: true,
            selectedValue: item[`${data?.id}`],
            values: data?.items?.length
              ? data.items.map((value: any) => ({
                  label: value?.name,
                  value: value?.name,
                }))
              : [],
            index,
            operation: '',
            data,
            isModernDropdown: true,
          };
        } else {
          return {
            type: 'text',
            rows: tableItemsProperty_.length,
            text: item[`${data?.id}`],
            index,
            data,
            hasFormulaError: false,
            nonEditable: !item?.id,
            hideTooltip: true,
          };
        }
      });
      cells.push(...propertyCells);
    }

    return {
      rowId: index,
      height: ROW_HEIGHT,
      itemId: item?.id,
      itemName: item?.name,
      reorderable: true,
      cells,
    };
  });

  rows.push(...dataRows);
  return rows;
};

const getColumns = (tableProperty_: any, tableReference: any) => {
  const propertyColumns = (tableProperty_?.length ? tableProperty_ : []).map((data: any) => ({
    columnId: `${data?.id}`,
    name: data?.name,
    width: calculatePropertyColumnWidth(data?.name),
    resizable: true,
    reorderable: true,
  }));

  const fixedWidth =
    NAME_COLUMN_MIN_WIDTH + propertyColumns.reduce((sum: number, col: any) => sum + col.width, 0);
  const extraSpace = tableReference > fixedWidth ? tableReference - fixedWidth : 0;

  return [
    {
      columnId: 'name',
      width: NAME_COLUMN_MIN_WIDTH + extraSpace,
      resizable: true,
    },
    ...propertyColumns,
  ];
};

// Optimized reorder array function
const reorderArray = <T extends {}>(array: T[], idxs: number[], to: number) => {
  if (!array?.length || !idxs?.length) return [];

  // Use Set for faster lookups
  const indexSet = new Set(idxs);
  const movedElements = array.filter((_, index) => indexSet.has(index));

  const minIndex = Math.min(...idxs);
  const targetIndex = minIndex < to ? to + 1 : to - idxs.filter((index) => index < to).length;

  const leftSide = array.filter((_, index) => index < targetIndex && !indexSet.has(index));
  const rightSide = array.filter((_, index) => index >= targetIndex && !indexSet.has(index));

  const reorderedArray = [...leftSide, ...movedElements, ...rightSide];
  return reorderedArray.map((item: any) => item.id);
};

export { headerRow, getRows, getColumns, reorderArray, clearCache };
